// Node data arriving from outside the editor (the workflow generator, saved graphs) can have
// `inputCount` and `inputs` disagree — e.g. `{ inputCount: 0, inputs: [] }`, which used to be
// coerced to `inputCount: 1` while `inputs` stayed empty, and the render then indexed past the
// array. These helpers make the pair consistent before the node renders.

const nextId = (taken, prefix) => {
  let index = taken.length + 1;
  let id = `${prefix}-${index}`;
  while (taken.includes(id)) {
    index += 1;
    id = `${prefix}-${index}`;
  }
  return id;
};

// The first input is "input"; later ones are "input 2", "input 3", … — which is also how the
// Python node exposes them (as `input`, `input_2`, …).
const labelFor = (index) => (index === 0 ? "input" : `input ${index + 1}`);

/**
 * Coerce `inputs` / `inputCount` into a consistent pair.
 *
 * - Existing inputs are kept (and given an id/label if missing).
 * - The returned count always equals the number of inputs, so nothing can index past the end.
 * - When `inputCount` exceeds the array, entries are synthesised.
 * - `minCount` enforces a floor (a Python node needs at least the implicit `input`).
 *
 * @param {object} data Node data.
 * @param {object} [options]
 * @param {boolean} [options.truncate] Honour a smaller inputCount by dropping trailing inputs
 *   (used when the user lowers the count); otherwise a longer array wins.
 * @param {number} [options.minCount] Smallest allowed number of inputs.
 * @returns {{inputs: object[], inputCount: number}}
 */
export const normalizeNodeInputs = (data = {}, { truncate = false, minCount = 0 } = {}) => {
  const rawInputs = Array.isArray(data?.inputs) ? data.inputs.filter(Boolean) : [];

  const inputs = rawInputs.map((input, index) => ({
    id: input.id || `input-${index + 1}`,
    label: input.label ?? labelFor(index),
  }));

  const declared = Number(data?.inputCount);
  const hasDeclared = Number.isFinite(declared) && declared >= 0;

  const floor = Math.max(0, Number(minCount) || 0);
  const target = hasDeclared ? declared : inputs.length;
  const count = Math.max(truncate ? target : Math.max(target, inputs.length), floor);

  // Shrink when asked to.
  inputs.length = Math.min(inputs.length, count);

  // Grow to match the count.
  while (inputs.length < count) {
    const takenIds = inputs.map((input) => input.id);
    inputs.push({
      id: nextId(takenIds, "input"),
      label: labelFor(inputs.length),
    });
  }

  return { inputs, inputCount: inputs.length };
};

/** True when stored node data doesn't satisfy the invariants and should be rewritten. */
export const inputsNeedRepair = (data, normalized) => {
  if (!Array.isArray(data?.inputs)) return true;
  if (data.inputs.length !== normalized.inputs.length) return true;
  if (Number(data?.inputCount) !== normalized.inputCount) return true;

  // The first input must be the implicit "input".
  if (normalized.inputs.length > 0 && data.inputs[0]?.label !== normalized.inputs[0].label) {
    return true;
  }

  return false;
};

export default normalizeNodeInputs;
